import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import TopBar from '../components/TopBar';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATIC_HEADER = { cta_url: "/donate" };
const STATIC_FOOTER = { contact_email: "info@securethevotemd.com" };

const ContentPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    window.scrollTo(0, 0);
    axios.get(`${API_URL}/api/pages/${slug}`)
      .then(res => { setPage(res.data); setLoading(false); })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [slug]);

  // Determine hero mode:
  // - featured_image set = full-width hero image (the image IS the hero, no overlay, no title on top)
  // - og_image set = standard red overlay hero with background image
  // - neither = solid maroon hero with title
  const hasFullWidthHero = page?.featured_image;
  const hasOverlayHero = !hasFullWidthHero;

  return (
    <div className="min-h-screen bg-white" data-testid="content-page">
      <TopBar />
      <Header config={STATIC_HEADER} />

      {/* FULL-WIDTH HERO IMAGE MODE — the image replaces the title banner entirely */}
      {!loading && hasFullWidthHero && page && (
        <div className="pt-[50px]" data-testid="page-hero-fullwidth">
          <img
            src={page.featured_image}
            alt={page.title}
            className="w-full block"
            style={{ maxHeight: '80vh', objectFit: 'cover', objectPosition: 'center' }}
            data-testid="page-hero-image"
          />
        </div>
      )}

      {/* STANDARD RED OVERLAY HERO MODE — default for most pages */}
      {hasOverlayHero && (
        <div className="relative pt-[50px]" data-testid="page-hero">
          <div
            className="absolute inset-0"
            style={{
              backgroundColor: '#9E1B32',
              ...(page?.og_image ? {
                backgroundImage: `url('${page.og_image}')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              } : {})
            }}
          />
          <div className="absolute inset-0" style={{ backgroundColor: 'rgba(158, 27, 50, 0.75)' }} />

          <div className="relative z-10 py-20 sm:py-28">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl text-center">
              <motion.h1
                className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight"
                style={{ fontFamily: "'Playfair Display', serif" }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                data-testid="page-title"
              >
                {loading ? '' : (page?.title || 'Page Not Found')}
              </motion.h1>
              <motion.div
                className="w-16 h-1 bg-[#F6BF58] mx-auto mt-6"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Page Content */}
      <main className="py-12 sm:py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          {loading && (
            <div className="space-y-4">
              <div className="h-6 bg-slate-100 rounded animate-pulse w-3/4" />
              <div className="h-4 bg-slate-100 rounded animate-pulse w-full" />
              <div className="h-4 bg-slate-100 rounded animate-pulse w-5/6" />
              <div className="h-4 bg-slate-100 rounded animate-pulse w-full" />
              <div className="h-4 bg-slate-100 rounded animate-pulse w-2/3" />
            </div>
          )}

          {notFound && (
            <div className="text-center py-16" data-testid="page-not-found">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Page Not Found</h2>
              <p className="text-slate-600">This page hasn't been created yet or the URL is incorrect.</p>
            </div>
          )}

          {page && (
            <motion.article
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              data-testid="page-content"
            >
              <div
                className="prose prose-lg max-w-none
                  prose-headings:text-slate-900 prose-headings:font-bold
                  prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                  prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                  prose-p:text-slate-600 prose-p:leading-relaxed prose-p:mb-4
                  prose-a:text-[#9E1B32] prose-a:no-underline hover:prose-a:underline
                  prose-strong:text-slate-900
                  prose-ul:my-4 prose-li:text-slate-600 prose-li:leading-relaxed
                  prose-ol:my-4
                  prose-img:rounded-lg prose-img:shadow-md
                  prose-hr:my-8 prose-hr:border-slate-200
                  [&_iframe]:w-full [&_iframe]:rounded-lg"
                dangerouslySetInnerHTML={{ __html: page.content }}
                data-testid="page-body"
              />
            </motion.article>
          )}
        </div>
      </main>

      <Footer config={STATIC_FOOTER} />
    </div>
  );
};

export default ContentPage;
